import {
  createContext,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { useAgentChat } from "./useAgentChat";
import type { AgentChatState } from "../ui/aiAssist/types";

/**
 * One chat transcript shared by every assistant surface mounted under the same
 * provider. The docked drawer and the full-screen dialog both read this, so the
 * conversation (entries, conversationId, streaming) carries over instantly when
 * the user expands the drawer to full screen and back.
 *
 * `useAgentChatContext` / `useAgentDraft` fall back to a fresh local state when
 * no provider is mounted, so the standalone `/ai-assist` route and unit tests
 * that mock `useAgentChat` keep working without a wrapper. `useAgentChat` has no
 * effects, so the idle fallback instance is cheap.
 */
const AgentChatContext = createContext<AgentChatState | null>(null);

type DraftState = readonly [string, Dispatch<SetStateAction<string>>];

/** The typed-but-unsent composer draft, shared so it survives drawer <-> full-screen. */
const AgentDraftContext = createContext<DraftState | null>(null);

export function AgentChatProvider({ children }: Readonly<{ children: ReactNode }>) {
  const chat = useAgentChat();
  const [draft, setDraft] = useState("");
  const draftValue = useMemo<DraftState>(() => [draft, setDraft], [draft]);
  return (
    <AgentChatContext.Provider value={chat}>
      <AgentDraftContext.Provider value={draftValue}>{children}</AgentDraftContext.Provider>
    </AgentChatContext.Provider>
  );
}

export function useAgentChatContext(): AgentChatState {
  const shared = useContext(AgentChatContext);
  const local = useAgentChat();
  return shared ?? local;
}

export function useAgentDraft(): DraftState {
  const shared = useContext(AgentDraftContext);
  const [draft, setDraft] = useState("");
  const local = useMemo<DraftState>(() => [draft, setDraft], [draft]);
  return shared ?? local;
}
